import * as Sentry from '@sentry/react'
import type { ErrorBoundaryProps } from '@sentry/react/dist/errorboundary'
import {
  ErrorBoundary,
  withErrorBoundary,
} from '@sentry/react/dist/errorboundary'
import type { ProfilerProps } from '@sentry/react/dist/profiler'
import { withProfiler } from '@sentry/react/dist/profiler'
import { Integrations } from '@sentry/tracing'
import type React from 'react'

import isOnePlatformEnabled from '../isOnePlatformEnabled'

// Adding a declariation for tt to avoid a build error.
declare global {
  interface Window {
    twentythree: any
  }
}

const isLocal =
  !window.twentythree ||
  (window.twentythree && window.twentythree.environment === 'local')
const SentryService = {
  captureException: (
    exception: Error,
    hint?: Parameters<(typeof Sentry)['captureException']>[1]
  ): string => {
    if (!isLocal && isOnePlatformEnabled) {
      return Sentry.captureException(exception, hint)
    }
    console.error(exception, hint)
    return ''
  },
  init: (dsn: string): void => {
    if (!isLocal && isOnePlatformEnabled) {
      Sentry.init({
        dsn,
        integrations: [new Integrations.BrowserTracing()],
        release: process.env.RELEASE_VERSION || 'local',
        environment: window.twentythree.environment || 'local',
        tracesSampleRate: isLocal ? 1.0 : 0.2,
        debug: isLocal,
      })
    }
  },
  setUser: (user: any): void => {
    Sentry.setUser(user)
  },

  ErrorBoundary: (...args: any): React.Component<ErrorBoundaryProps> => {
    if (!isLocal && isOnePlatformEnabled) {
      return new ErrorBoundary({
        ...args,
      })
    }
    return args.props.children
  },
  withErrorBoundary: <P extends Record<string, any>>(
    WrappedComponent: React.ComponentType<P>,
    errorBoundaryOptions: ErrorBoundaryProps
  ): React.FC<P> => {
    if (!isLocal && isOnePlatformEnabled) {
      const opt = {
        ...errorBoundaryOptions,
      }
      return withErrorBoundary(WrappedComponent, opt)
    }
    return WrappedComponent as React.FC<P>
  },

  withProfiler: <P extends Record<string, any>>(
    WrappedComponent: React.ComponentType<P>,
    options?: Pick<
      Partial<ProfilerProps>,
      Exclude<keyof ProfilerProps, 'updateProps'>
    >
  ): React.FC<P> => {
    if (!isLocal && isOnePlatformEnabled) {
      const opt = {
        ...options,
      }
      return withProfiler(WrappedComponent, opt)
    }
    return WrappedComponent as React.FC<P>
  },
}
export default SentryService
